import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Library, Plus, Pencil } from 'lucide-react'
import { useAdminBenchmarks } from '@/hooks/useBenchmarkQueries'
import { ErrorState } from '@/components/shared/ErrorState'
import { EmptyState } from '@/components/shared/EmptyState'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/utils'
import { ROUTE_STATE_LABEL } from '@/components/vendor-content/RouteStatus'
import { vendorRoutingUiEnabled } from '@/components/vendor-content/routing-ui-flag'

const STATUSES = ['all', 'draft', 'published', 'suppressed'] as const
// KAN-822 T9 — the routed history view (R3): candidates with routing history.
// A tab, not a status: a routed candidate keeps its benchmark status.
type StatusTab = (typeof STATUSES)[number] | 'routed'

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'outline',
  published: 'default',
  suppressed: 'destructive',
}

const SOURCE_KIND_LABEL: Record<string, string> = {
  manual: 'Manual',
  rss_auto: 'RSS',
  analyst_auto: 'Analyst',
  api_auto: 'API',
}

const SOURCE_KIND_CLASS: Record<string, string> = {
  manual: 'bg-primary/10 text-primary',
  rss_auto: 'bg-muted text-muted-foreground',
  analyst_auto: 'bg-muted text-muted-foreground',
  api_auto: 'bg-muted text-muted-foreground',
}

export function BenchmarksAdminPage() {
  const [status, setStatus] = useState<StatusTab>('all')
  // KAN-822 T9 — dark build flag: the Routed tab and route labels.
  const routingUi = vendorRoutingUiEnabled()
  const tabs: readonly StatusTab[] = routingUi ? [...STATUSES, 'routed'] : STATUSES
  // The admin list API ignores `status=all`; send only when filtering.
  const filters =
    status === 'routed' ? { routed: 'true' as const } : status === 'all' ? {} : { status }
  const { data, isLoading, isError, refetch } = useAdminBenchmarks(filters)

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Library className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Benchmarks</h1>
            <p className="text-sm text-muted-foreground">
              Curated analyst, vendor, and research-firm reports shown to CISOs.
            </p>
          </div>
        </div>
        <Button asChild size="sm">
          <Link to="/admin/benchmarks/new" className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" aria-hidden />
            New benchmark
          </Link>
        </Button>
      </header>

      <div
        role="tablist"
        aria-label="Filter by status"
        className="inline-flex w-fit items-center gap-1 rounded-md border bg-muted p-1 text-xs"
      >
        {tabs.map((s) => (
          <button
            key={s}
            role="tab"
            type="button"
            aria-selected={status === s}
            onClick={() => setStatus(s)}
            className={
              'rounded px-3 py-1 capitalize transition-colors ' +
              (status === s
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          title="Failed to load benchmarks"
          message="We couldn't reach the admin benchmarks API. Try again in a moment."
          onRetry={() => refetch()}
        />
      ) : (data?.items ?? []).length === 0 ? (
        <EmptyState
          icon={Library}
          title="No benchmarks yet"
          description={
            status === 'all'
              ? 'Create a benchmark to start the library.'
              : `No ${status} benchmarks in the library.`
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 font-semibold">Title</th>
                <th className="p-3 font-semibold">Publisher</th>
                <th className="p-3 font-semibold">Type</th>
                <th className="p-3 font-semibold">Source</th>
                <th className="p-3 font-semibold">Published</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((b) => (
                <tr key={b.id} className="border-b last:border-b-0 hover:bg-muted/20">
                  <td className="p-3 font-medium">
                    <Link to={`/admin/benchmarks/${b.id}/edit`} className="hover:underline">
                      {b.title}
                    </Link>
                  </td>
                  <td className="p-3 text-muted-foreground">{b.publisher}</td>
                  <td className="p-3">
                    <span className="rounded bg-muted px-2 py-0.5 text-xs capitalize">
                      {b.reportType.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="p-3">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${SOURCE_KIND_CLASS[b.sourceKind] ?? 'bg-muted text-muted-foreground'}`}
                    >
                      {SOURCE_KIND_LABEL[b.sourceKind] ?? b.sourceKind}
                    </span>
                  </td>
                  <td className="p-3 text-muted-foreground">{formatDate(b.publishedAt)}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={STATUS_VARIANT[b.status] ?? 'outline'} className="capitalize">
                        {b.status}
                      </Badge>
                      {routingUi && b.routeState && (
                        <Badge variant="outline">Routed · {ROUTE_STATE_LABEL[b.routeState] ?? b.routeState}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <Button asChild variant="ghost" size="sm">
                      <Link
                        to={`/admin/benchmarks/${b.id}/edit`}
                        className="inline-flex items-center gap-1"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                        Edit
                      </Link>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
